// oxlint-disable no-console
import { fetchWithOptions } from './fetch';
import { parseJWTFromCookie } from './jwtAuthTools';

const isDev = process.env.NODE_ENV === 'development';

export enum Loggable {
  /**
   * Something in the program is DEFINITELY NOT WORKING AS INTENDED. The
   * scenario should be investigated because the bug CAN AND SHOULD be fixed.
   */
  BugActionable = 'BugActionable',

  /**
   * Any possibly interesting events that are NOT ACTIONABLE BUGS (see the
   * `BugActionable` variant for that).
   *
   * For example:
   *
   * - User attempting to do something that the program INTENTIONALLY DOES NOT
   *   SUPPORT, or
   *
   * - User doing something that the program DOES SUPPORT but a data point is
   *   desirable regardless.
   */
  UsageAnalytic = 'UsageAnalytic',
}

interface ISerializable {
  /** Get something that can be serialized. */
  getSerializable: () => unknown;
}

export class UsageAnalyticDatapoint implements ISerializable {
  #serializable: unknown;
  constructor(serializable: unknown) {
    this.#serializable = serializable;
  }

  public getSerializable(): unknown {
    return this.#serializable;
  }
}

export class ActionableErrorContext implements ISerializable {
  #reactErrorInfo: null | React.ErrorInfo;
  #scenario: string;

  /**
   * @param reactErrorInfo
   *
   *        React component tree context if the error was caught by such
   *        capturing mechanism, or `null` if not.
   *
   * @param scenario
   *
   *        Description of what was being attempted when the error occurred.
   *        Always defined, never nullish; The point is to help locate,
   *        reproduce and fix the reported error. If it's not known what's
   *        causing an error, then the report would also be inactionable and
   *        thus should NOT be logged. In such case you should probably rewrite
   *        the smelly code that makes you want to log vaguely defined errors!
   *
   *        The `scenario` value is NOT intended to be displayed to end user.
   *        Rather, it serves as a technical identifier in telemetry while
   *        hopefully being somewhat descriptive at the same time, at least to
   *        those familiar with the codebase.
   */
  constructor(reactErrorInfo: null | React.ErrorInfo, scenario: string) {
    this.#reactErrorInfo = reactErrorInfo;
    this.#scenario = scenario;
  }

  public getSerializable(): unknown {
    return {
      reactErrorInfo: this.#reactErrorInfo,
      scenario: this.#scenario,
    };
  }
}

/**
 * In development build, make log to console. In production build, send to
 * backend. Graceful; Never throws.
 */
export function makeLog(variant: Loggable.BugActionable, error: unknown, context: ActionableErrorContext): void;
export function makeLog(variant: Loggable.UsageAnalytic, datapoint: UsageAnalyticDatapoint): void;
export function makeLog(
  ...args:
    | [variant: Loggable.BugActionable, error: unknown, context: ActionableErrorContext]
    | [variant: Loggable.UsageAnalytic, datapoint: UsageAnalyticDatapoint]
): void {
  try {
    const [variant, arg1, arg2] = args;

    if (isDev) {
      if (variant === Loggable.BugActionable) {
        console.error(variant, arg1, arg2);
      } else {
        console.debug(variant, arg1, arg2);
      }
      return;
    }

    const neurojwt = parseJWTFromCookie();
    if (!neurojwt) {
      return;
    }

    const clientSessionContext = {
      timestamp: new Date().toISOString(),
      location: window.location.href,
    };

    if (variant === Loggable.BugActionable) {
      const error: unknown = arg1;
      const errorContext: ActionableErrorContext = arg2;
      const body: string = JSON.stringify({
        clientSessionContext,
        errorChain: error instanceof Error ? expandErrorIntoChain(error) : [],
        errorContext: errorContext.getSerializable(),
      });
      // oxlint-disable-next-line promise/prefer-await-to-callbacks
      fetchWithOptions('/api/log/v2/error', { neurojwt }, { body }).catch((err: unknown) =>
        console.log('Could not report error', err),
      );
    } else {
      const datapoint: UsageAnalyticDatapoint = arg1;
      const body: string = JSON.stringify({
        clientSessionContext,
        datapoint: datapoint.getSerializable(),
      });
      // oxlint-disable-next-line promise/prefer-await-to-callbacks
      fetchWithOptions('/api/log/v2/analytic', { neurojwt }, { body }).catch((err: unknown) =>
        console.log('Could not report analytics', err),
      );
    }
  } catch (err) {
    if (isDev) {
      console.error('fallthrough error in makeLog', err);
    }
  }
}

/**
 * Expand an error into a chain of causes.
 */
const expandErrorIntoChain = (
  error: Error,
): Array<{ name: null | string; message: null | string; stack: null | string }> => {
  const errors: ReturnType<typeof expandErrorIntoChain> = [];
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  while (error) {
    errors.push(
      /*
       * Pick non-enumerable properties (to be included in serializable
       * payload).
       *
       * Passive-aggressive language rant: NULL `name` or `message` shall
       * indicate that something else than an `Error` was passed in as an
       * `Error`. Not only is it amazing that you have this hidden control
       * flow in the language (i.e. can throw stuff around across the call
       * stack, instead of always returning values call by call), but you can
       * also throw anything instead of just things with a uniform (`Error`)
       * interface! And of course the "type system" is only optional and thus
       * useless!
       */
      {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        name: error.name ?? null,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        message: error.message ?? null,
        stack: error.stack ?? null,
      },
    );
    if (error.cause instanceof Error) {
      error = error.cause;
    } else {
      break;
    }
  }
  return errors;
};
